import classnames from 'classnames';
import { forwardRef } from 'react';
import type { FC, MouseEvent, ReactNode, Ref } from 'react';

import Icon from '../Icon';
import type { IconClassName } from '../Icon';

import ButtonBase from './Base';
import { BUTTON_SIZE, BUTTON_TYPE } from './button-constants';
import type { GetButtonProps } from './Base';
import type { ButtonSize, ButtonType } from './button-constants';

export type ButtonProps = {
  /**
   * This isn't a "prop", but needs to be included here for TS to understand that `ref` can be passed.
   */
  ref?: Ref<HTMLButtonElement>;
  /**
   * Which type (style) of button to use.
   */
  type?: ButtonType;
  /**
   * Additional class name(s) to merge onto the rendered button, after the generated `fossa-button` classes.
   */
  className?: string;
  /**
   * Which size should the button be.
   */
  size?: ButtonSize;
  /**
   * If a given icon should be rendered to the left of props.children.
   */
  icon?: IconClassName;
  /**
   * If an icon is provided, should it spin
   */
  iconHasSpin?: boolean;
  /**
   * If the button should be disabled or not.
   */
  isDisabled?: boolean;
  /**
   * If the button should be 100% width;
   */
  isFullWidth?: boolean;
  /**
   * Should the HTML type attribute be "submit" (for forms).
   */
  isSubmitButton?: boolean;
  /**
   * HTML Title attribute, useful for labeling icons/buttons without text.
   */
  title?: string;
  /**
   * Adds a `data-test-id` attribute, useful for selenium testing.
   */
  testId?: string;
  /**
   * The content of the button.
   */
  children?: ReactNode;
  /**
   * Click handler for the button.
   */
  onClick: (event: MouseEvent<HTMLElement>) => void;
};

//  forwardRef understands that ref isn't really a prop, but ButtonProps needs
//  to include it for TS to understand that it's a valid prop.
type ButtonPropsWithoutRef = Omit<ButtonProps, 'ref'>;

/**
 * Generic FOSSA Button component.
 */
const Button: FC<ButtonProps> = forwardRef<HTMLButtonElement, ButtonPropsWithoutRef>(
  (
    {
      type = BUTTON_TYPE.PRIMARY,
      size = BUTTON_SIZE.SMALL,
      className,
      icon,
      iconHasSpin,
      title,
      testId,
      isDisabled,
      isFullWidth,
      isSubmitButton,
      children,
      onClick,
    }: ButtonProps,
    ref?: Ref<HTMLButtonElement>,
  ) => (
    <ButtonBase
      type={type}
      size={size}
      title={title}
      testId={testId}
      isDisabled={isDisabled}
      isFullWidth={isFullWidth}
      isSubmitButton={isSubmitButton}
      onClick={onClick}
    >
      {(getButtonProps: GetButtonProps) => {
        const buttonProps = getButtonProps();
        return (
          // Note: disabling, because `getButtonProps` returns a button type.
          // eslint-disable-next-line react/button-has-type
          <button
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...buttonProps}
            className={classnames(buttonProps.className, className)}
            ref={ref}
          >
            <span className="fossa-button__contents">
              {icon && (
                <span
                  className={classnames('fossa-button__icon', {
                    'fossa-button__icon--solo': !children, // Remove margin if only child
                  })}
                >
                  <Icon icon={icon} hasSpin={iconHasSpin} />
                </span>
              )}
              <span className="fossa-button__text">{children}</span>
            </span>
          </button>
        );
      }}
    </ButtonBase>
  ),
);

export default Button;
